/**
 * The sign-in URL for a visitor who reached a page that needs a session,
 * remembering where they were going.
 *
 * The Shopify OAuth callback lands a logged-out merchant on
 * /settings/integrations?shopify=success&shop=… or, mid-setup, on
 * /onboarding?platform=shopify&success=true&shop=…; without `redirect=` that
 * landing (its toast, its wizard step, the store it names) was lost the moment
 * the login form appeared. The login page reads `redirect` back, keeps it only
 * when it is a same-site path, and continues there after sign-in.
 */
export function loginPathFor(pathname: string, search: string): string {
  const wanted = `${pathname}${search}`;
  return wanted === '/' || wanted.startsWith('/login')
    ? '/login'
    : `/login?redirect=${encodeURIComponent(wanted)}`;
}

/** sessionStorage key — set by the avatar Logout action before tokens clear. */
const INTENTIONAL_LOGOUT_KEY = 'bbx_intentional_logout';

/** Call before clearing the session so the next bounce to /login stays bare. */
export function markIntentionalLogout(): void {
  try {
    sessionStorage.setItem(INTENTIONAL_LOGOUT_KEY, '1');
  } catch {
    // Private mode / blocked storage — logout still proceeds.
  }
}

/**
 * True once per intentional logout. Consumed so a later cold visit to an app
 * path still gets `?redirect=` from loginPathFor.
 */
export function consumeIntentionalLogout(): boolean {
  try {
    if (sessionStorage.getItem(INTENTIONAL_LOGOUT_KEY) !== '1') return false;
    sessionStorage.removeItem(INTENTIONAL_LOGOUT_KEY);
    return true;
  } catch {
    return false;
  }
}

/** Where a sign-in lands when it was given nowhere usable to go. */
const DEFAULT_DESTINATION = '/business';

/**
 * `raw` when it is a path on this site, else null.
 *
 * `?redirect=` is whatever a link says, so honouring anything else would turn
 * sign-in into an open redirect. A single leading "/" is not enough on its
 * own: browsers read "\" as "/" and drop tabs and newlines while parsing, so
 * "/\evil.example" and "/<tab>/evil.example" name another site by the time
 * they are followed — and react-router, when history.pushState refuses such a
 * URL, falls back to window.location.assign. So the path is also resolved the
 * way the browser will resolve it, and kept only if it stays on this origin.
 */
function sameSitePath(raw: string | null | undefined): string | null {
  if (!raw || !raw.startsWith('/') || raw.startsWith('//')) return null;
  try {
    return new URL(raw, window.location.origin).origin === window.location.origin ? raw : null;
  } catch {
    return null;
  }
}

/**
 * Where to land after signing in: the `?redirect=` path when it is on this
 * site, else /business. Used by the login form and by the /login route for a
 * visitor who is already signed in.
 */
export function safeRedirect(raw: string | null | undefined): string {
  return sameSitePath(raw) ?? DEFAULT_DESTINATION;
}

/**
 * The destination across a "Continue with Google" round trip.
 *
 * Google sign-in leaves for accounts.google.com and comes back to
 * /auth/callback with only a token, so the `?redirect=` the login page was
 * holding (a Shopify return, a deep link) was gone and the merchant landed on
 * /business. The button parks it here (sessionStorage: this tab, this browser)
 * and AuthCallback takes it back once the session exists. It is validated
 * again as it is read, read at most once, dropped when the sign-in fails, and
 * ignored once it is older than a sign-in takes — fifteen minutes, as for the
 * Shopify connect intent in lib/shopifyHandoff.
 */
const GOOGLE_REDIRECT_KEY = 'bbx_google_signin_redirect';
const GOOGLE_REDIRECT_MAX_AGE_MS = 15 * 60 * 1000;

/** As the Google button leaves the page. Anything unusable clears an earlier value instead. */
export function rememberGoogleSignInRedirect(raw: string | null | undefined, now: number = Date.now()): void {
  const path = sameSitePath(raw);
  try {
    if (path) sessionStorage.setItem(GOOGLE_REDIRECT_KEY, JSON.stringify({ path, at: now }));
    else sessionStorage.removeItem(GOOGLE_REDIRECT_KEY);
  } catch {
    // Storage can be unavailable (private mode, blocked site data); Google
    // sign-in then lands where it always has.
  }
}

/** The remembered destination, or null. Removed as it is read, so it is followed at most once. */
export function takeGoogleSignInRedirect(now: number = Date.now()): string | null {
  let stored: string | null;
  try {
    stored = sessionStorage.getItem(GOOGLE_REDIRECT_KEY);
    sessionStorage.removeItem(GOOGLE_REDIRECT_KEY);
  } catch {
    return null;
  }
  return freshPath(stored, now);
}

/** The sign-in this destination was waiting for failed. */
export function forgetGoogleSignInRedirect(): void {
  try {
    sessionStorage.removeItem(GOOGLE_REDIRECT_KEY);
  } catch {
    // Nothing reachable to forget.
  }
}

function freshPath(stored: string | null, now: number): string | null {
  if (!stored) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(stored);
  } catch {
    return null;
  }
  if (typeof parsed !== 'object' || parsed === null || !('path' in parsed) || !('at' in parsed)) return null;
  const { path, at } = parsed;
  if (typeof path !== 'string' || typeof at !== 'number') return null;
  const age = now - at;
  return age >= 0 && age <= GOOGLE_REDIRECT_MAX_AGE_MS ? sameSitePath(path) : null;
}
